/**
 * The frame most WEB steps draw in, and the text roles that repeat across them.
 *
 * BgColor and AccentColor are authored per adventure, so they cannot be static
 * classes. `stepVars` sets them on the root as CSS custom properties and the
 * classes below read them back — which keeps every other rule a class and
 * leaves one place to look when a colour is wrong.
 *
 * Steps that draw their own frame (Doors, KeyHandoff, Difficulty) still use
 * `stepVars`; they just skip SECTION_CLASS.
 */
import type { CSSProperties } from "react";

/**
 * Hex-with-alpha suffixes are computed here rather than with color-mix in a
 * class, so the alpha stays legible next to the colour it applies to.
 */
export function stepVars({
  accent,
  bg,
  btnText = "#f2dba6",
}: {
  accent: string;
  bg?: string;
  btnText?: string;
}): CSSProperties {
  return {
    "--sm-bg": bg,
    "--sm-accent": accent,
    "--sm-accent-glow": `${accent}22`,
    "--sm-accent-soft": `${accent}59`,
    "--sm-accent-edge": `${accent}6b`,
    "--sm-accent-strong": `${accent}80`,
    "--sm-btn-text": btnText,
  } as CSSProperties;
}

/**
 * Full-height flat backdrop. Callers add their own padding.
 *
 * The accent washes that used to sit in the corners reads as haze behind the
 * copy, so the funnel is flat — the intro (a clip on pure black) already was,
 * and the rest now matches it.
 */
/** The phone-shaped frame every screen plays in: full screen on a phone, a
 *  centred portrait column (at most 0.62 of the height wide) on anything
 *  wider. */
export const STEP_FRAME_CLASS =
  "fixed inset-0 mx-auto w-full overflow-clip bg-black [contain:layout] [container-type:inline-size] max-w-[calc(100dvh*0.62)]";

export const SECTION_CLASS =
  "relative flex h-[100dvh] items-center justify-center overflow-hidden font-sans " +
  "bg-[var(--sm-bg)]";

/** The dossier card the copy sits on. */
export const CARD_CLASS =
  "relative z-[2] mx-auto max-w-[432px] rounded-xl border border-[var(--sm-accent-edge)] " +
  "bg-[rgb(18_16_18/0.85)] px-[26px] pt-[30px] pb-[26px] text-center " +
  "shadow-[0_26px_70px_rgba(0,0,0,.6),inset_0_0_50px_rgba(0,0,0,.4)]";

export const TITLE_CLASS =
  "mb-4 text-[22px] leading-[1.32] font-bold text-[#f4eddb]";

export const MESSAGE_CLASS =
  "my-[7px] text-[18px] leading-[1.55] text-[#ddd3bf]";

export const SUBMESSAGE_CLASS =
  "mt-4 text-[16.5px] leading-[1.55] text-[#a99d86] italic";

/**
 * The action language from the spy15 mockup: deep red, gold hairline. Size and
 * font are left to the composers below.
 */
export const ACTION_BTN_CLASS =
  "cursor-pointer border border-[var(--sm-accent-strong)] " +
  "bg-[linear-gradient(180deg,#7d1d18,#511110)] text-[var(--sm-btn-text)] " +
  "shadow-[0_6px_18px_rgba(0,0,0,.45),inset_0_1px_0_rgba(255,255,255,.08)] " +
  "transition-[filter,translate] duration-200 hover:-translate-y-px hover:brightness-110 active:translate-y-px " +
  "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0";

/** The wide "go on" button. */
export const PRIMARY_BTN_CLASS = `${ACTION_BTN_CLASS} min-w-[160px] rounded-[7px] px-[22px] py-[13px] text-[15.5px] tracking-[.2px]`;

/** Side-by-side choice buttons (Start, Order). */
export const CHOICE_BTN_CLASS = `${ACTION_BTN_CLASS} flex-1 min-w-[148px] rounded-lg px-4 py-3 text-[14.5px] font-semibold tracking-[0.2px]`;

/** The quiet underlined "actually, take me back" link. */
export const LINK_BTN_CLASS =
  "mx-auto mt-[22px] mb-[2px] block cursor-pointer border-none bg-none text-sm text-[#9b8f79] underline";

/**
 * The reveal timings, shared by Start and Order.
 *
 * These name `translate` and `scale`, not `transform`: Tailwind v4's
 * translate/scale/rotate utilities set those standalone CSS properties, so a
 * transition on `transform` matches nothing and the element snaps into place.
 * Each property keeps its own duration and easing, which a single
 * `transition-[opacity,translate]` would flatten.
 */
export const FLY_IN_CLASS =
  "[transition:opacity_.5s_ease,translate_.6s_cubic-bezier(.2,.75,.25,1)]";
export const POP_IN_CLASS =
  "[transition:opacity_.3s_ease,scale_.4s_cubic-bezier(.2,1.5,.4,1)]";
